import $ from 'jquery';
import debounce from 'just-debounce';

export default class Map {
  constructor(el) {
    this.el = el;
    this.map = null;
    this.center = null;

    this.container = this.el.querySelector('[data-map-container]');
    this.container.innerHTML = '';

    const addressHtml = document.createElement('div');
    addressHtml.innerHTML = this.el.getAttribute('data-map-address');
    this.address = addressHtml.innerText;

    this.zoom = parseInt(this.el.getAttribute('data-map-zoom'), 10);
    if (isNaN(this.zoom)) this.zoom = 13;

    this.colors = {
      a: this.el.getAttribute('data-map-color-a'),
      b: this.el.getAttribute('data-map-color-b'),
      c: this.el.getAttribute('data-map-color-c'),
      d: this.el.getAttribute('data-map-color-d'),
      e: this.el.getAttribute('data-map-color-e'),
      f: this.el.getAttribute('data-map-color-f'),
    };
    this.resize = this.resize.bind(this);
  }

  createMap() {
    return this.geolocate()
      .done((results) => {
        this.map = new google.maps.Map(this.container, {
          center: results[0].geometry.location,
          clickableIcons: false,
          disableDefaultUI: true,
          disableDoubleClickZoom: true,
          gestureHandling: 'none',
          keyboardShortcuts: false,
          maxZoom: this.zoom,
          minZoom: this.zoom,
          scrollWheel: false,
          styles: this.getMapStyles(),
          zoom: this.zoom,
          zoomControl: false,
        });
        this.center = this.map.getCenter();

        const marker = new google.maps.Marker({
          clickable: false,
          map: this.map,
          position: this.center,
        });

        google.maps.event.addDomListener(
          window,
          'resize',
          debounce(this.resize, 250, true, true),
        );
      })
      .fail((status) => {
        const usageLimits = 'https://developers.google.com/maps/faq#usagelimits';
        let errorMessage;

        switch (status) {
          case 'ZERO_RESULTS':
            errorMessage = `<p>Unable to find the address:</p> ${this.address}`;
            break;
          case 'OVER_QUERY_LIMIT':
            errorMessage = `
              <p>Unable to load Google Maps, you have reached your usage limit.</p>
              <p>
                Please visit
                <a href="${usageLimits}" target="_blank">${usageLimits}</a>
                for more details.
              </p>
            `;
            break;
          default:
            errorMessage = 'Unable to load Google Maps.';
            break;
        }

        this.displayErrorInThemeEditor(errorMessage, status);
      });
  }

  geolocate() {
    const deferred = $.Deferred();
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: this.address }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {
        deferred.reject(status);
      }

      deferred.resolve(results);
    });

    return deferred;
  }

  resize() {
    if (this.map) {
      google.maps.event.trigger(this.map, 'resize');
      this.map.setCenter(this.center);
    }
  }

  unload() {
    if (this.map) {
      google.maps.event.clearListeners(this.map, 'resize');
    }
  }

  getMapStyles() {
    return [
      { elementType: 'geometry', stylers: [{ color: this.colors.e }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: this.colors.a }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: this.colors.e }] },
      { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative.country', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative.locality', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: this.colors.d }] },
      { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: this.colors.c }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: this.colors.b }] },
      { featureType: 'road.highway.controlled_access', stylers: [{ visibility: 'off' }] },
      { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: this.colors.b }] },
      { featureType: 'road.local', elementType: 'labels.text.stroke', stylers: [{ color: this.colors.e }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: this.colors.f }] },
    ];
  }

  displayErrorInThemeEditor(errorMessage, status = '') {
    // Editor check
    const isThemeEditor = window.Shopify && window.Shopify.designMode;

    if (!isThemeEditor) {
      return;
    }

    this.container.innerHTML = `<div class="map-error-message">${errorMessage}</div>`;
    console.warn(this.container.innerText, status);
  }
}
